import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { AutoSuggest } from "../../../../components/common/form/AutoSuggest";
import { CButton } from "../../../../components/common/buttons";
import { useCustomSnackbar } from "../../../../utils/hooks/useCustomSnackbar";
import { listContractorAgreementForAvrModal } from "../services";

const useStyles = makeStyles({
  content: {
    minWidth: 350,
    paddingTop: 8,
  },
  field: {
    marginBottom: 20,
  },
  title: {
    color: "#0C4B86",
    fontWeight: 600,
  },
  btn: {
    height: "44px !important",
    minWidth: "120px !important",
    marginLeft: 12,
  },
});

const initialFormState = {
  contractor_agreement_list_id: "",
  avr_sum: "",
  is_for_full_agreement_sum: false,
};

const AddAvrModal = ({ open, onClose, remontId, onSubmit }) => {
  const classes = useStyles();
  const { addCloseSnack } = useCustomSnackbar();

  const [agreements, setAgreements] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadAgreements = useCallback(async () => {
    if (!remontId) {
      addCloseSnack("Не указан remontId");
      if (onClose) {
        onClose();
      }
      return;
    }

    setIsFetching(true);
    const res = await listContractorAgreementForAvrModal(remontId);
    setIsFetching(false);

    if (!res) {
      addCloseSnack("Не удалось загрузить список договоров");
      if (onClose) {
        onClose();
      }
      return;
    }

    setAgreements(res);
  }, [addCloseSnack, onClose, remontId]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormData(initialFormState);
    setAgreements([]);
    loadAgreements();
  }, [open, loadAgreements]);

  const handleChange = (key, value) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "is_for_full_agreement_sum" && value) {
        next.avr_sum = "";
      }

      return next;
    });
  };

  const validate = () => {
    if (!formData.contractor_agreement_list_id) {
      addCloseSnack("Выберите договор");
      return false;
    }

    if (!formData.is_for_full_agreement_sum) {
      if (
        formData.avr_sum === "" ||
        formData.avr_sum === null ||
        formData.avr_sum === undefined
      ) {
        addCloseSnack("Необходимо заполнить поле «Сумма АВР»");
        return false;
      }

      const sum = Number(formData.avr_sum);
      if (Number.isNaN(sum) || sum < 0) {
        addCloseSnack("Укажите корректную сумму АВР");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (!validate()) {
      return;
    }

    const body = {
      contractor_agreement_list_id: formData.contractor_agreement_list_id,
      avr_sum: formData.is_for_full_agreement_sum
        ? null
        : formData.avr_sum === ""
          ? null
          : Number(formData.avr_sum),
      is_for_full_agreement_sum: !!formData.is_for_full_agreement_sum,
    };

    setLoading(true);
    try {
      const result = onSubmit ? await onSubmit(body) : undefined;
      if (result !== false && onClose) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Typography className={classes.title}>Добавить АВР</Typography>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent className={classes.content}>
          <Box className={classes.field}>
            <AutoSuggest
              withMargin
              list={agreements}
              showKey="contractor_agreement_list_name"
              valueKey="contractor_agreement_list_id"
              label="Договор"
              required
              loading={isFetching}
              value={formData.contractor_agreement_list_id}
              onChange={(e) =>
                handleChange("contractor_agreement_list_id", e.target.value)
              }
            />
          </Box>

          <Box className={classes.field}>
            <TextField
              fullWidth
              type="number"
              label="Сумма АВР"
              variant="standard"
              required={!formData.is_for_full_agreement_sum}
              disabled={!!formData.is_for_full_agreement_sum}
              value={formData.avr_sum}
              inputProps={{ min: 0, step: 1 }}
              onChange={(e) => handleChange("avr_sum", e.target.value)}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={!!formData.is_for_full_agreement_sum}
                onChange={(e) =>
                  handleChange("is_for_full_agreement_sum", e.target.checked)
                }
              />
            }
            label="На всю сумму договора"
          />
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={loading}
          >
            Отменить
          </Button>
          <CButton
            type="submit"
            className={classes.btn}
            loading={loading}
            disabled={
              loading ||
              isFetching ||
              !formData.contractor_agreement_list_id ||
              (!formData.is_for_full_agreement_sum && formData.avr_sum === "")
            }
          >
            Сохранить
          </CButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

AddAvrModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  remontId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSubmit: PropTypes.func,
};

export default AddAvrModal;
